import type { PlanPriceDto } from '../../api/billingPayments'
import { resolvePlanCardsCopy, yearlySavingsRub } from '../../billing/planCatalogCopy'

type PlanPricingCardsProps = {
  catalog: PlanPriceDto[]
  currentPlan: string
  onSelectPlan: (plan: PlanPriceDto) => void
  formatRub: (amount: number) => string
  /** Доплата за заведение сверх базовых — для сравнения «Года» с помесячной оплатой. */
  addonVenueMonthlyRub: number
  /** Сколько заведений входит в «Год» без доплаты. */
  yearBaseVenues: number
}

const SOLD_PLANS = new Set(['start', 'month', 'year'])

const CheckIcon = () => (
  <svg className="market-pricing-card__icon market-pricing-card__icon--yes" viewBox="0 0 16 16" aria-hidden="true">
    <path d="M6.5 11.2 3.3 8l1-1 2.2 2.2 5.5-5.5 1 1-6.5 6.5z" fill="currentColor" />
  </svg>
)

const CrossIcon = () => (
  <svg className="market-pricing-card__icon market-pricing-card__icon--no" viewBox="0 0 16 16" aria-hidden="true">
    <path
      d="M4.5 4.5 8 8m0 0 3.5 3.5M8 8l3.5-3.5M8 8 4.5 11.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
)

type PricingContext = {
  monthMonthlyRub: number | null
  addonVenueMonthlyRub: number
  yearBaseVenues: number
}

/** Числа в подписях считаются из каталога, чтобы не разъезжаться с ценами при их смене. */
const priceForPlan = (
  plan: PlanPriceDto,
  formatRub: (amount: number) => string,
  context: PricingContext,
) => {
  if (plan.plan === 'start') {
    return {
      value: '0 ₽',
      period: 'навсегда',
      note: 'Для знакомства с кабинетом',
    }
  }
  if (plan.plan === 'year' && plan.yearly_rub != null) {
    const perMonth = Math.round(plan.yearly_rub / 12)
    const venues = Math.max(1, context.yearBaseVenues)
    const monthlyForSameVenues =
      context.monthMonthlyRub == null
        ? null
        : context.monthMonthlyRub + (venues - 1) * context.addonVenueMonthlyRub
    const savings =
      monthlyForSameVenues == null ? 0 : yearlySavingsRub(monthlyForSameVenues, plan.yearly_rub)
    const savingsNote =
      savings > 0 ? ` — экономия ≈ ${formatRub(Math.round(savings / 1_000) * 1_000)} в год` : ''
    return {
      value: formatRub(plan.yearly_rub),
      period: 'в год',
      note: `≈ ${formatRub(perMonth)}/мес${venues > 1 ? ` с ${venues} заведениями` : ''}${savingsNote}`,
    }
  }
  if (plan.plan === 'month') {
    return {
      value: formatRub(plan.monthly_rub),
      period: 'в месяц',
      note: `+${formatRub(context.addonVenueMonthlyRub)} в месяц за каждое доп. заведение`,
    }
  }
  return {
    value: formatRub(plan.monthly_rub),
    period: '/ мес',
    note: null,
  }
}

const ctaLabelForPlan = (plan: string) => {
  switch (plan) {
    case 'month':
      return 'Выбрать Месяц'
    case 'year':
      return 'Выбрать Год'
    default:
      return 'Выбрать тариф'
  }
}

export const PlanPricingCards = ({
  catalog,
  currentPlan,
  onSelectPlan,
  formatRub,
  addonVenueMonthlyRub,
  yearBaseVenues,
}: PlanPricingCardsProps) => {
  const soldCatalog = catalog.filter((plan) => SOLD_PLANS.has(plan.plan))
  const resolvedCards = resolvePlanCardsCopy(soldCatalog)
  const pricingContext: PricingContext = {
    monthMonthlyRub: soldCatalog.find((plan) => plan.plan === 'month')?.monthly_rub ?? null,
    addonVenueMonthlyRub,
    yearBaseVenues,
  }

  return (
    <section className="market-pricing" aria-labelledby="market-pricing-title">
      <div className="market-pricing__header">
        <div>
          <h2 id="market-pricing-title" className="market-pricing__title">
            Выберите тариф
          </h2>
          <p className="market-pricing__lead">
            Сравните возможности и подключите подходящий план.
            <br />
            Комиссия с сделок на всех тарифах — 0%.
          </p>
        </div>
      </div>

      <div className="market-pricing__grid">
        {soldCatalog.map((planPrice) => {
          const copy = resolvedCards.find((item) => item.plan === planPrice.plan)
          const isCurrent = planPrice.plan === currentPlan
          const isHighlighted = copy?.recommended === true || planPrice.plan === 'year'
          const price = priceForPlan(planPrice, formatRub, pricingContext)

          return (
            <article
              key={planPrice.plan}
              className={[
                'market-pricing-card',
                isHighlighted ? 'market-pricing-card--recommended' : '',
                isCurrent ? 'market-pricing-card--current' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <header className="market-pricing-card__header">
                {planPrice.plan === 'year' || isCurrent ? (
                  <div className="market-pricing-card__tags">
                    {planPrice.plan === 'year' ? (
                      <p className="market-pricing-card__ribbon">Выгоднее</p>
                    ) : null}
                    {isCurrent ? <p className="market-pricing-card__badge">Ваш тариф</p> : null}
                  </div>
                ) : null}
                <h3 className="market-pricing-card__name">{planPrice.display_name}</h3>
                <p className="market-pricing-card__tagline">{copy?.tagline ?? planPrice.description}</p>
              </header>

              <div className="market-pricing-card__price-block">
                <p className="market-pricing-card__price">
                  <span className="market-pricing-card__price-value">{price.value}</span>
                  <span className="market-pricing-card__price-period">{price.period}</span>
                </p>
                {price.note ? <p className="market-pricing-card__alt-price">{price.note}</p> : null}
              </div>

              <ul className="market-pricing-card__features">
                {(copy?.features ?? []).map((feature) => (
                  <li
                    key={feature.label}
                    className={[
                      'market-pricing-card__feature',
                      feature.included
                        ? 'market-pricing-card__feature--yes'
                        : 'market-pricing-card__feature--no',
                      feature.highlight ? 'market-pricing-card__feature--highlight' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {feature.included ? <CheckIcon /> : <CrossIcon />}
                    <span>{feature.label}</span>
                  </li>
                ))}
              </ul>

              <footer className="market-pricing-card__footer">
                {isCurrent ? (
                  <button type="button" className="echat-btn echat-btn--ghost" disabled>
                    Текущий тариф
                  </button>
                ) : planPrice.plan === 'start' ? (
                  <p className="market-pricing-card__hint">Базовый тариф для знакомства с сервисом</p>
                ) : (
                  <button
                    type="button"
                    className={
                      planPrice.plan === 'year'
                        ? 'echat-btn echat-btn--primary market-pricing-card__cta'
                        : 'echat-btn echat-btn--ghost market-pricing-card__cta'
                    }
                    onClick={() => onSelectPlan(planPrice)}
                  >
                    {ctaLabelForPlan(planPrice.plan)}
                  </button>
                )}
              </footer>
            </article>
          )
        })}
      </div>
    </section>
  )
}
